import { Port } from '../Map/AISMapContext';
import { useShipSelection } from '../Map/contexts';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { cn, formatVesselName, isNonEmpty } from '@ais-ui/utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
  Progress,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  InfiniteScroll,
  SearchInput,
} from '@ais-ui/ui-core';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useTimeUntil } from '../conversions/useTimeUntil';
import {
  NextPortArrivalWithDate,
  useNextportPortGet,
  useBulkShipInfo,
} from '@ais-ui/api';
import {
  PiClock,
  PiDotsThreeVertical,
  PiMagnifyingGlassPlus,
  PiPath,
  PiSortAscending,
} from 'react-icons/pi';
import { useDebounce } from '@ais-ui/custom-hooks';
import { ErrorDisplay } from '@ais-ui/ui-core';
import {
  extractTargetInfos,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  ShipInfo,
  TargetTypeEnum,
  useSession,
} from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';
import {
  formatProbability,
  probabilityDotClass,
} from '../utils/probabilityFormatting';
import { useDrawRouteToPort } from '../utils/useDrawRouteToPort';

// Sort types for the vessel arrivals
type SortType = 'eta' | 'probability';

interface PortDetailsPanelProps {
  port: Port;
}

export const PortDetailsPanel = React.memo(
  ({ port }: PortDetailsPanelProps) => {
    const [search, setSearch] = useState<string>('');
    const bouncedSearchString = useDebounce(search, 200);
    const [sortType, setSortType] = useState<SortType>('eta');

    return (
      <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden space-y-4">
        <div className="flex gap-2 px-px pt-1">
          <SearchInput
            id="search"
            placeholder={'Search vessels (min. 3 characters)'}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            onClear={() => setSearch('')}
          />
          <DropdownMenu>
            <Tooltip>
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10">
                    <PiSortAscending className="size-5" />
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <TooltipContent>Sort vessels</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortType('eta')}>
                Sort by arrival time
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortType('probability')}>
                Sort by prediction percentage
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <ArrivingVessels
          port={port}
          bouncedSearchString={bouncedSearchString}
          sortType={sortType}
        />
      </div>
    );
  },
);

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow truncate">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface IdentificationProps {
  port: Port;
}

export const PortIdentification = ({ port }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <IdentificationItem
        label={messages.portDetailsBaseNameLabel}
        value={port.name}
      />
      <IdentificationItem
        label={messages.portDetailsBaseLocodeLabel}
        value={port.locode}
      />
      <IdentificationItem
        label={messages.portDetailsBaseCountryLabel}
        value={port.country}
      />
    </div>
  );
};

interface ArrivingVesselsProps {
  port: Port;
  bouncedSearchString: string;
  sortType: SortType;
}

interface ArrivingVesselsFallBackProps {
  error: any;
}

const ArrivingVesselsFallBack = ({ error }: ArrivingVesselsFallBackProps) => {
  if (error?.response?.parsedBody?.code === 422) {
    return (
      <ErrorDisplay
        srcImage="/422_not_supported_error_bro.avif"
        message="Vessel arrival predictions not supported for Port"
        className="h-full pb-40"
      />
    );
  }

  return (
    <ErrorDisplay
      message="Couldn't fetch vessel arrival predictions for the port"
      className="h-full pb-40"
    />
  );
};

const ArrivingVessels = React.memo(
  ({ port, bouncedSearchString, sortType }: ArrivingVesselsProps) => {
    const [selectedImo, setSelectedImo] = useState<number | undefined>(
      undefined,
    );
    const { settings } = useSettings();
    const inclAnchorages =
      settings['ribbonPlugin.settings']?.nextport_incl_anchorages ?? false;

    const {
      data,
      isLoading: isLoadingNextport,
      isError,
      error,
    } = useNextportPortGet(port.locode, inclAnchorages);

    const imos = useMemo(
      () => data?.records?.map((arrival) => arrival.imo) || [],
      [data?.records],
    );

    const { data: bulkShipData, isLoading: isLoadingBulkShipInfo } =
      useBulkShipInfo(imos);

    const shipInfoMap = useMemo(() => {
      if (!bulkShipData) return new Map();
      const map = new Map();
      bulkShipData.forEach((shipInfo) => {
        if (shipInfo.imo) {
          map.set(shipInfo.imo, shipInfo);
        }
      });
      return map;
    }, [bulkShipData]);

    // Combine filtering logic into a single useMemo to avoid multiple re-renders
    const filteredArrivals = useMemo(() => {
      if (!data?.records) return [];

      // Only filter if search string has 3 or more characters
      if (!bouncedSearchString || bouncedSearchString.length < 3) {
        return data.records;
      }

      // Filter based on search string - search through ship names and IMOs
      const searchLower = bouncedSearchString.toLowerCase();
      const filtered = data.records.filter((arrival) => {
        const shipInfo = shipInfoMap.get(arrival.imo);
        const shipName = shipInfo?.name || shipInfo?.ship?.name || '';
        const imo = arrival.imo.toString();

        return (
          shipName.toLowerCase().includes(searchLower) ||
          imo.includes(searchLower)
        );
      });
      return filtered;
    }, [data?.records, bouncedSearchString, shipInfoMap]);

    // Make sure we re-sort whenever sortType changes
    const sortedArrivals = useMemo(() => {
      if (!filteredArrivals || filteredArrivals.length === 0) return [];

      const sorted = [...filteredArrivals];

      if (sortType === 'eta') {
        return sorted.sort((a, b) => {
          // Handle cases where eta could be undefined
          if (!a.eta) return 1;
          if (!b.eta) return -1;
          return new Date(a.eta).getTime() - new Date(b.eta).getTime();
        });
      } else if (sortType === 'probability') {
        return sorted.sort((a, b) => {
          // Handle cases where probability is undefined
          const probA = a.probability ?? 0;
          const probB = b.probability ?? 0;
          return probB - probA; // Sort by highest probability first
        });
      }

      return sorted;
    }, [filteredArrivals, sortType]);

    const isLoading = isLoadingNextport || isLoadingBulkShipInfo;

    // Derive the inspected arrival from selection state, falling back to the
    // first entry in the current sort order — no effect needed to keep this
    // in sync. Arrivals are keyed by IMO (unique per vessel).
    const selectedIndex = Math.max(
      0,
      sortedArrivals.findIndex((arrival) => arrival.imo === selectedImo),
    );
    const selectedArrival = sortedArrivals[selectedIndex];
    const rankLabel = `Arrival ${selectedIndex + 1} of ${sortedArrivals.length}`;

    return (
      <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden">
        <div className="mb-4">
          <h3 className="text-lg font-medium">
            Vessel arrival predictions ({sortedArrivals.length})
          </h3>
          <p className="text-xs text-muted-foreground">
            {inclAnchorages
              ? 'Includes anchorage areas'
              : 'Excludes anchorage areas'}
          </p>
          <Separator className="mt-2" />
        </div>
        {isLoading && <LoadingSpinner />}
        {isError && <ArrivingVesselsFallBack error={error} />}
        {!isLoading && sortedArrivals.length > 0 && (
          <div className="flex flex-1 flex-col gap-4 overflow-hidden">
            {selectedArrival && (
              <NextArrivalDetailCard
                key={selectedArrival.imo}
                arrival={selectedArrival}
                port={port}
                shipInfo={shipInfoMap.get(selectedArrival.imo)}
                rankLabel={rankLabel}
              />
            )}

            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex items-center justify-between px-1 pb-1">
                <p className="text-xs text-muted-foreground">
                  {sortedArrivals.length} inbound · click to inspect
                </p>
                <p className="text-xs text-muted-foreground">ETA</p>
              </div>
              <InfiniteScroll
                items={sortedArrivals}
                limit={30}
                observeResize={true}
                className="flex-1 divide-y overflow-y-auto rounded-lg border shadow-xs"
                renderItem={(arrival: NextPortArrivalWithDate) => (
                  <NextArrivalRow
                    key={arrival.imo}
                    arrival={arrival}
                    shipInfo={shipInfoMap.get(arrival.imo)}
                    isSelected={arrival.imo === selectedArrival?.imo}
                    onSelect={() => setSelectedImo(arrival.imo)}
                  />
                )}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

/**
 * Resolves a `useBulkShipInfo` record's display fields, falling back to the
 * arrival record's IMO when ship info hasn't loaded yet.
 */
const useArrivalDisplayInfo = (
  arrival: NextPortArrivalWithDate,
  shipInfo: any,
) => {
  // shipInfo has both flattened fields and a nested ship object
  const ship = shipInfo?.ship || shipInfo;

  const displayImo = ship?.imo || arrival.imo;
  const displayMmsi = ship?.mmsi;
  const displayName = formatVesselName(ship?.name);

  return { displayImo, displayMmsi, displayName };
};

/**
 * Looks up an AIS ship target by MMSI and zooms/selects it on the map,
 * surfacing toasts for the missing-MMSI / lookup-failure cases.
 */
const useZoomToShip = (mmsi: number | undefined) => {
  const { toast } = useToast();
  const { olSession } = useSession();
  const { setZoomToShip } = useShipSelection();
  const { messages } = useLocalization();

  return useCallback(async () => {
    if (!mmsi) {
      toast({
        title: messages.genericToastTitleWarning,
        description: 'MMSI not available for this vessel',
        variant: 'warning',
      });
      return;
    }

    try {
      const result = await getTargetInfoByMMSI(olSession, mmsi);
      const extractedTargetInfo = extractTargetInfos(result);

      if (
        extractedTargetInfo.extractedTargetInfoType !== TargetTypeEnum.Ship ||
        extractedTargetInfo.extractedTargetInfo.length === 0
      ) {
        toast({
          title: messages.genericToastTitleError,
          description: 'Could not find ship',
          variant: 'destructive',
        });
        return;
      }

      const aisShip = {
        callSign: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo)
          .callSign,
        imo: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).imo,
        mmsi: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).mmsi,
        name: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).name,
        type: 'TT_SHIP',
      };

      setZoomToShip(aisShip);
    } catch (error) {
      if (error instanceof InvalidMMSIError) {
        toast({
          title: messages.genericToastTitleWarning,
          description: 'Invalid MMSI number',
          variant: 'warning',
        });
        return;
      }
      throw error;
    }
  }, [mmsi, olSession, setZoomToShip, toast, messages]);
};

interface NextArrivalDetailCardProps {
  arrival: NextPortArrivalWithDate;
  port: Port;
  shipInfo?: any;
  /** e.g. "Arrival 10 of 12" — the selected entry's position in the current sort order. */
  rankLabel: string;
}

/** The currently inspected arrival, shown as a highlighted detail card. */
const NextArrivalDetailCard = ({
  arrival,
  port,
  shipInfo,
  rankLabel,
}: NextArrivalDetailCardProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const etaCountdown = useTimeUntil(arrival.eta);
  const { displayImo, displayMmsi, displayName } = useArrivalDisplayInfo(
    arrival,
    shipInfo,
  );
  const onZoomToShip = useZoomToShip(displayMmsi);
  const { isRouteShown, toggleRoute, isCalculatingRoute, canDrawRoute } =
    useDrawRouteToPort({
      mmsi: displayMmsi?.toString(),
      shipName: displayName,
      nextport: arrival,
      portName: port?.name,
    });

  const probabilityPercent = arrival.probability
    ? arrival.probability * 100
    : 0;

  return (
    <div className="rounded-lg border bg-muted/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            {rankLabel}
          </p>
          <p className="mt-0.5 truncate text-base font-semibold">
            {displayName || (
              <span className="text-muted-foreground">IMO: {displayImo}</span>
            )}
          </p>
          <p className="truncate text-sm text-muted-foreground">
            MMSI: {displayMmsi || 'Unknown'} · IMO: {displayImo || 'Unknown'}
          </p>
        </div>
        <p className="shrink-0 text-xl font-bold">
          {formatProbability(arrival.probability)}
        </p>
      </div>

      <Progress value={probabilityPercent} className="mt-2 h-1.5" />

      <Separator className="my-3" />

      <div className="flex items-center justify-between gap-2 text-sm">
        <div className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
          <PiClock className="size-4 shrink-0" />
          <span className="truncate">
            ETA {formatDateTimeWithTimeZone(arrival.eta, true)}
            {etaCountdown && ` · ${etaCountdown}`}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="shrink-0 rounded-md border p-1 text-foreground hover:bg-accent"
              aria-label="Arrival actions"
            >
              <PiDotsThreeVertical className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onZoomToShip} disabled={!displayMmsi}>
              <PiMagnifyingGlassPlus className="mr-2 size-4" />
              Zoom to ship
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={toggleRoute}
              disabled={isCalculatingRoute || !canDrawRoute}
            >
              <PiPath className="mr-2 size-4" />
              {isRouteShown ? 'Remove path to port' : 'Draw path to port'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

interface NextArrivalRowProps {
  arrival: NextPortArrivalWithDate;
  shipInfo?: any;
  isSelected: boolean;
  onSelect: () => void;
}

/** A single inbound vessel, shown as a clickable list row that inspects it in the detail card above. */
const NextArrivalRow = ({
  arrival,
  shipInfo,
  isSelected,
  onSelect,
}: NextArrivalRowProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { displayImo, displayName } = useArrivalDisplayInfo(arrival, shipInfo);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent/60',
        isSelected && 'bg-accent/60',
      )}
    >
      <span className={probabilityDotClass(arrival.probability)}></span>
      <span className="w-14 shrink-0 tabular-nums text-muted-foreground">
        {formatProbability(arrival.probability)}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span
          className={cn(
            'truncate font-medium',
            isSelected ? 'text-primary' : 'text-foreground',
          )}
        >
          {displayName || `IMO: ${displayImo}`}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {displayImo}
        </span>
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatDateTimeWithTimeZone(arrival.eta, true)}
      </span>
    </button>
  );
};
